/**
 * Client authentication utility for Admin Secret Token management.
 */

const TOKEN_STORAGE_KEY = 'echocondria_admin_token';

/**
 * Retrieve the saved admin secret token from localStorage.
 */
export function getAdminToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Save admin token to localStorage.
 */
export function setAdminToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token.trim());
  } catch (e) {
    console.warn('Could not store admin token in localStorage', e);
  }
}

/**
 * Clear admin token from localStorage.
 */
export function clearAdminToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    console.warn('Could not remove admin token from localStorage', e);
  }
}

export interface VerifyAuthResult {
  success: boolean;
  error?: string;
  locked?: boolean;
  attemptsRemaining?: number;
}

/**
 * Verify token against the backend endpoint.
 */
export async function verifyAdminToken(token: string): Promise<VerifyAuthResult> {
  const cleanToken = token.trim();
  if (!cleanToken) {
    return { success: false, error: 'Token cannot be empty' };
  }

  try {
    const res = await fetch('/api/auth/verify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${cleanToken}`,
      },
      body: JSON.stringify({ token: cleanToken }),
    });

    if (res.ok) {
      return { success: true };
    }

    const data: any = await res.json().catch(() => ({}));
    return {
      success: false,
      error: data?.error || (res.status === 403 ? 'Your IP address has been locked out.' : 'Invalid admin secret token'),
      locked: data?.locked ?? (res.status === 403),
      attemptsRemaining: data?.attemptsRemaining,
    };
  } catch (err: any) {
    return { success: false, error: err?.message || 'Verification request failed' };
  }
}

/**
 * Return authorization headers for mutating API requests.
 */
export function getAuthHeaders(): HeadersInit {
  const token = getAdminToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}
